/**
 * meetingTelemetry — one registry for meeting/AV telemetry event names, plus
 * the helpers that keep thrown errors safe for telemetry.
 *
 * Track ids are emitted raw, not hashed: they are already stored raw elsewhere
 * and the raw value is what cross-references a row against LiveKit's own
 * records. Only error messages get sanitized, because a DOMException message
 * can carry a raw device label.
 *
 * Why this exists: event names were scattered string literals across the
 * meeting cubits and sinks, and the GA and Sentry names for the same condition
 * had already drifted (Sentry emits `meeting_speech_unreachable`, GA emits
 * `meeting_speech_reachability_problem_escalated` for the same escalation).
 * New meeting/AV events reference a constant here instead of a literal, so
 * there is one list to audit and one place to keep the names consistent.
 *
 * Two groups below:
 * - EXISTING: names already emitted in the codebase. Registered here so the
 *   registry is the single source of truth. Do NOT rename these; downstream
 *   GA/BigQuery queries and the admin inspector already key on them.
 * - NEW: the gaps the AV telemetry plan fills. Add a constant here before you
 *   emit it.
 *
 * See docs/prds/av-live-meeting-telemetry-plan.md.
 */
export const MeetingTelemetryEvent = {
  // --- EXISTING local-track lifecycle (already emitted; do not rename) ---
  /** A local track (audio or video) was published to the room. */
  localTrackPublished: 'meeting_local_track_published',
  /** The local audio track specifically was published. */
  audioTrackPublished: 'meeting_audio_track_published',
  /** A local track was unpublished. */
  localTrackUnpublished: 'meeting_local_track_unpublished',
  /** The last local audio publication dropped to zero mid-session. */
  audioTrackLost: 'meeting_audio_track_lost',

  // --- NEW local-track lifecycle (gaps the plan fills) ---
  /** About to call createLocalAudioTrack (the browser getUserMedia path). */
  localAudioTrackCreateAttempted: 'local_audio_track_create_attempted',
  /** createLocalAudioTrack resolved with a track. */
  localAudioTrackCreated: 'local_audio_track_created',
  /** createLocalAudioTrack threw: the browser never yielded a mic track. */
  localAudioTrackCreateFailed: 'local_audio_track_create_failed',
  /** About to publish the local audio track to LiveKit. */
  localAudioTrackPublishAttempted: 'local_audio_track_publish_attempted',
  /**
   * A local audio publish failed. `failure_stage: 'publish'` is a clean LiveKit
   * publish rejection (we already had a track). `failure_stage:
   * 'set_microphone_enabled'` is the combined getUserMedia+publish path, so it
   * mixes create and publish failures — discriminate with `error_name`
   * (`NotAllowedError`/`NotFoundError` => create/permission, not LiveKit).
   */
  localAudioTrackPublishFailed: 'local_audio_track_publish_failed',
  /** The underlying MediaStreamTrack ended (device unplugged, revoked). */
  localAudioTrackEnded: 'local_audio_track_ended',
  /** Periodic, rate-limited local audio health sample during a session. */
  localAudioHealthSample: 'local_audio_health_sample',

  // --- AV check + repair/rejoin (ties to the rejoin blank-screen bug) ---
  // Note: AV-check start/capture already emit under room_avcheck_* names. The
  // per-step failure, av_repair_rejoin_failed, and av_repair_rejoin_blank_screen
  // events ship with the rejoin fix (ticket 2), which adds the gate detector.
  /** The AV check passed and the user continued into the meeting. */
  avCheckPassed: 'av_check_passed',
  /** The user clicked rejoin after repairing their mic. */
  avRepairRejoinClicked: 'av_repair_rejoin_clicked',

  // --- Content / slide video (distinct from LiveKit audio) ---
  // Raw media state only; sessionizing (did it start on this slide, how long
  // it buffered) is left to the analytics side, which has the timestamps.
  /** The deck video began loading a source. */
  contentVideoLoadAttempted: 'content_video_load_attempted',
  /**
   * The deck video reported it can play. Note: `canplay` can fire more than
   * once per source (after a seek or a buffer refill), so this is not a
   * one-row-per-successful-load counter.
   */
  contentVideoCanPlay: 'content_video_can_play',
  /** play() rejected (non-autoplay) or the element errored. */
  contentVideoPlayFailed: 'content_video_play_failed',
  /** The deck video is buffering (waiting for data); once per stall. */
  contentVideoBuffering: 'content_video_buffering',
  /** The browser is fetching but data stopped arriving (network trouble). */
  contentVideoStalled: 'content_video_stalled',
  /** play() was blocked by the browser autoplay policy. */
  contentVideoAutoplayBlocked: 'content_video_autoplay_blocked',
  /** The deck video played to the end. */
  contentVideoEnded: 'content_video_ended',
} as const

export type MeetingTelemetryEventName =
  (typeof MeetingTelemetryEvent)[keyof typeof MeetingTelemetryEvent]

/**
 * The fields `MeetingCubit.logEvent` stamps on every meeting event. New event
 * params are merged on top of these; they are documented here so a reader knows
 * what every meeting row already carries without re-reading the cubit.
 */
export interface MeetingTelemetryCommonFields {
  room_id: string
  assignment_id: string
  section_id: string
  slide_deck_id: string
  active_slide: number
}

/**
 * sanitizeErrorMessage — reduce a thrown error to a bounded, length-capped
 * message string. This caps length only; it does not redact content, so a short
 * device label (`Microphone (Blue Yeti)`) still passes through. Pair it with
 * {@link errorName}: the error name is the low-cardinality, label-free field to
 * group and alert on, and the message is a best-effort, size-limited detail.
 * Do not treat the message as guaranteed free of hardware labels.
 */
export function sanitizeErrorMessage(
  error: unknown,
  maxLength = 120
): string | undefined {
  if (error === null || error === undefined) return undefined
  let message: string
  if (error instanceof Error) {
    message = error.message
  } else if (typeof error === 'string') {
    message = error
  } else if (
    // MediaError, DOMException-likes: not Error instances but carry a message.
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    message = error.message
  } else {
    message = String(error)
  }
  if (!message) return undefined
  return message.slice(0, maxLength)
}

/**
 * errorName — the DOMException / Error name (`NotAllowedError`,
 * `NotFoundError`, ...), which is the low-cardinality, safe part of a thrown
 * media error and the field worth grouping on.
 */
export function errorName(error: unknown): string | undefined {
  if (error instanceof Error) return error.name
  // DOMException / MediaError-likes carry a name but aren't Error instances.
  if (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof error.name === 'string'
  ) {
    return error.name
  }
  return undefined
}
